import { useState, useEffect } from 'react';
import {
  fetchOffers,
  deleteOffer,
  type OfferFilters,
} from '@/api/marketplace-offers.api';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CheckCircle,
  XCircle,
  Trash2,
  Clock,
  ArrowLeftRight,
  DollarSign,
  AlertCircle,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function MarketplaceOffers() {
  const [offers, setOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<OfferFilters>({
    limit: 20,
  });
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const loadOffers = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentFilters = loadMore ? { ...filters, cursor: nextCursor } : filters;
      const data = await fetchOffers(currentFilters);
      if (loadMore) {
        setOffers((prev) => [...prev, ...(data?.data || [])]);
      } else {
        setOffers(data?.data || []);
      }
      setNextCursor(data?.nextCursor || null);
      setHasMore(data?.hasMore || false);
    } catch (error) {
      console.error('Failed to load offers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, [filters]);

  const openDeleteDialog = (offer: any) => {
    setSelectedOffer(offer);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedOffer) return;
    try {
      await deleteOffer(selectedOffer._id, deleteReason);
      // Remove offer from local state immediately
      setOffers((prev) => prev.filter((o) => o._id !== selectedOffer._id));
      setDeleteDialogOpen(false);
      setSelectedOffer(null);
      setDeleteReason('');
    } catch (error) {
      console.error('Failed to delete offer:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      pending: { variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      accepted: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      declined: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      counter_offered: { variant: 'outline', icon: ArrowLeftRight, color: 'text-blue-600' },
      expired: { variant: 'outline', icon: AlertCircle, color: 'text-gray-600' },
      withdrawn: { variant: 'outline', icon: XCircle, color: 'text-gray-500' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${config.color}`} />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getOfferTypeBadge = (offerType: string) => {
    if (offerType === 'cash') {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-green-600" />
          Cash
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        <ArrowLeftRight className="w-3 h-3 text-purple-600" />
        Exchange
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6 h-full overflow-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketplace Offers</h1>
          <p className="text-muted-foreground">
            View and manage all marketplace offers
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter offers by status and type</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, status: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="counter_offered">Counter Offered</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="withdrawn">Withdrawn</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.offerType || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, offerType: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Offer Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="exchange">Exchange</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Offers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Offers</CardTitle>
          <CardDescription>
            {offers.length} offer(s) found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && offers.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : offers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No offers found
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Listing</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {offers.map((offer) => (
                  <TableRow key={offer._id}>
                    <TableCell className="max-w-xs">
                      <div className="flex items-center gap-2">
                        {offer.listing?.media?.[0]?.url && (
                          <img
                            src={offer.listing.media[0].url}
                            alt={offer.listing?.title}
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <div className="font-medium line-clamp-1">
                          {offer.listing?.title || 'Deleted listing'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={offer.buyer?.profile || '/avatar.png'}
                          alt={offer.buyer?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        {offer.buyer?.username || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={offer.seller?.profile || '/avatar.png'}
                          alt={offer.seller?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        {offer.seller?.username || '-'}
                      </div>
                    </TableCell>
                    <TableCell>{getOfferTypeBadge(offer.offerType)}</TableCell>
                    <TableCell>
                      {offer.offerType === 'cash' ? (
                        <span className="font-semibold">
                          {offer.listing?.currency || '$'}{offer.cashAmount}
                        </span>
                      ) : (
                        <span className="text-purple-600">Exchange</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(offer.status)}</TableCell>
                    <TableCell>
                      {new Date(offer.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDeleteDialog(offer)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
          {hasMore && !loading && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => loadOffers(true)}>
                Load More
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Delete Offer</DialogTitle>
            <DialogDescription>
              Remove this offer. Active offers will be declined automatically.
            </DialogDescription>
          </DialogHeader>

          {selectedOffer && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground">Listing</Label>
                <div className="font-medium">{selectedOffer.listing?.title || 'Deleted listing'}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Offer</Label>
                <div className="font-medium">
                  {selectedOffer.offerType === 'cash'
                    ? `$${selectedOffer.cashAmount}`
                    : 'Exchange offer'}{' '}
                  by {selectedOffer.buyer?.username}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deleteReason">Reason *</Label>
                <Textarea
                  id="deleteReason"
                  placeholder="Explain why this offer is being removed..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!deleteReason.trim()}
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
